import { MinLengthArray, containsAtLeast } from '@omnea-app/packages/universal/general/arrays'
import { normaliseString, removeDiacritics } from '@omnea-app/packages/universal/string-utils/removeDiacritics'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { AtLeastOne } from '@omnea-app/packages/universal/utility-types/utilityTypes'

export type IndexRange = readonly [startIndex: number, endIndex: number]

export interface FuzzySearchResult<Item> {
  item: Item
  highlightedRanges: IndexRange[]
  score: ResultScore
}

export const fuzzySearchList = <Item>(
  list: Item[],
  query: string,
  getTextToBeSearched: (item: NoInfer<Item>) => string
): FuzzySearchResult<Item>[] => {
  const normalisedQueryChars = normaliseString(query).split('')
  if (!containsAtLeast(1)(normalisedQueryChars)) return []

  return list
    .map((item) => {
      const textToBeSearched = getTextToBeSearched(item)

      // Find the character ranges in the text that match the query
      const highlightedRanges = findFuzzyMatches(textToBeSearched, normalisedQueryChars)
      if (!highlightedRanges) return null

      const searchResult: FuzzySearchResult<Item> = {
        item,
        highlightedRanges,
        score: getRangesScore(highlightedRanges),
      }

      return searchResult
    })
    .filter(isDefined) // Remove items without a match
    .sort(compareResults) // Sort by best match first
}

export const findFuzzyMatches = (
  text: string,
  normalisedQueryChars: MinLengthArray<string, 1>
): MinLengthArray<IndexRange, 1> | null => {
  const textChars = removeDiacritics(text).toLowerCase().split('')
  if (!containsAtLeast(1)(textChars)) return null

  const matchedIndices = getMatchedIndices(textChars, normalisedQueryChars)

  // If there are no matches, return an empty array
  if (!matchedIndices) return null

  const ranges = getMatchedRanges(matchedIndices)
  if (!containsAtLeast(1)(ranges)) return null

  return ranges
}

const getMatchedIndices = (textChars: MinLengthArray<string, 1>, queryChars: MinLengthArray<string, 1>): boolean[] | null => {
  const mutableQueryChars = [...queryChars]

  const matchedIndices = textChars
    /**
     * First, for each character in the string,
     * find all the characters in the text that have a sequential match in the query
     *
     * e.g. 'helloo', 'hloo' => [true, false, true, false, true, true]
     *                           h     e      l     l      o     o
     *                                              ^ note that the second 'l' is not included
     */
    .map((char) => {
      if (!containsAtLeast(1)(mutableQueryChars)) return false // query string is empty

      if (char !== mutableQueryChars[0]) return false // no match

      mutableQueryChars.shift() // After a match is found, remove the first character from the query
      return true
    })

  // If after iterating through the text there are still unmatched characters left in the query
  // Then it's not considered a match
  if (mutableQueryChars.length !== 0) return null

  return matchedIndices
}

const getMatchedRanges = (matchedIndices: boolean[]): IndexRange[] => {
  return (
    matchedIndices
      /**
       * Get matching sequences of characters
       *
       * e.g. [TRUE, false, TRUE, false, TRUE, TRUE] => [[0], [2], [4, 5]]
       *       (0)   1      (2)   3      (4    5)
       */
      .reduce((sequences, hasMatch, index) => {
        if (!hasMatch) {
          sequences.push([]) // If there is no match, start a new sequence
          return sequences
        }

        const lastRange = sequences.at(-1)
        if (!lastRange) {
          sequences.push([index])
        } else {
          lastRange.push(index)
        }

        return sequences
      }, [] as number[][])
      .filter(containsAtLeast(1))
      .map(getRangeForSequence)
  )
}

/**
 * Return the start and end indices of each sequence
 *
 * e.g. [[0], [2], [4, 5]] => [[0, 0], [2, 2], [4, 5]]
 */
const getRangeForSequence = (sequence: AtLeastOne<number>): IndexRange => {
  const first = sequence[0]
  // oxlint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
  const last = sequence.at(-1)! // The sequence has at least one element, so this casting is safe
  return [first, last]
}

interface ResultScore {
  longestSequence: number
  firstMatchIndex: number
}

export const getRangesScore = (ranges: IndexRange[]): ResultScore => {
  const rangeLengths = ranges.map(([start, end]) => end - start + 1)

  return {
    longestSequence: Math.max(0, ...rangeLengths),
    firstMatchIndex: ranges[0]?.[0] ?? Infinity,
  }
}

/**
 * Compare two range scores, best to worst
 */
export const compareRangeScores = (a: ResultScore, b: ResultScore): number => {
  if (a.longestSequence !== b.longestSequence) {
    return b.longestSequence - a.longestSequence
  }

  return a.firstMatchIndex - b.firstMatchIndex
}

const compareResults = (a: FuzzySearchResult<unknown>, b: FuzzySearchResult<unknown>): number => {
  return compareRangeScores(a.score, b.score)
}
